import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'

// Using Tailwind violet/purple palette
const violet100 = '#ede9fe',
  violet200 = '#ddd6fe',
  violet300 = '#c4b5fd',
  violet400 = '#a78bfa',
  violet500 = '#8b5cf6',
  violet600 = '#7c3aed',
  violet700 = '#6d28d9',
  violet800 = '#5b21b6',
  violet900 = '#4c1d95'

const purple100 = '#f5f3ff',
  purple200 = '#ede9fe',
  purple300 = '#d8b4fe',
  purple400 = '#c084fc',
  purple500 = '#a855f7',
  purple600 = '#9333ea',
  purple700 = '#7e22ce',
  purple800 = '#6b21a8',
  purple900 = '#581c87'

const invalid = '#dc2626', // tailwind red-600
  darkBackground = violet200,
  highlightBackground = violet100,
  background = '#ffffff',
  tooltipBackground = purple200,
  selection = violet300,
  cursor = violet900

export const witerLightTheme = EditorView.theme(
  {
    '&': {
      color: violet900,
      backgroundColor: background
    },

    '.cm-content': {
      caretColor: cursor
    },

    '.cm-cursor, .cm-dropCursor': { borderLeftColor: cursor },
    '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection':
      { backgroundColor: selection },

    '.cm-panels': { backgroundColor: darkBackground, color: violet700 },
    '.cm-panels.cm-panels-top': { borderBottom: '2px solid black' },
    '.cm-panels.cm-panels-bottom': { borderTop: '2px solid black' },

    '.cm-searchMatch': {
      backgroundColor: violet400 + '59', // Added opacity
      outline: `1px solid ${violet600}`
    },
    '.cm-searchMatch.cm-searchMatch-selected': {
      backgroundColor: purple300
    },

    '.cm-activeLine': { backgroundColor: highlightBackground },
    '.cm-selectionMatch': { backgroundColor: purple200 },

    '&.cm-focused .cm-matchingBracket, &.cm-focused .cm-nonmatchingBracket': {
      outline: `1px solid ${violet600}`
    },

    '&.cm-focused .cm-matchingBracket': {
      backgroundColor: violet100
    },

    '.cm-gutters': {
      backgroundColor: violet100,
      color: violet900,
      border: 'none'
    },

    '.cm-activeLineGutter': {
      backgroundColor: highlightBackground
    },

    '.cm-foldPlaceholder': {
      backgroundColor: 'transparent',
      border: 'none',
      color: purple400
    },

    '.cm-tooltip': {
      border: 'none',
      backgroundColor: tooltipBackground
    },
    '.cm-tooltip .cm-tooltip-arrow:before': {
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent'
    },
    '.cm-tooltip .cm-tooltip-arrow:after': {
      borderTopColor: tooltipBackground,
      borderBottomColor: tooltipBackground
    },
    '.cm-tooltip-autocomplete': {
      '& > ul > li[aria-selected]': {
        backgroundColor: highlightBackground,
        color: violet700
      }
    }
  },
  { dark: false }
)

export const witerLightHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: violet600 },
  {
    tag: [t.name, t.deleted, t.character, t.propertyName, t.macroName],
    color: purple500
  },
  { tag: [t.variableName], color: purple400 },
  { tag: [t.function(t.variableName)], color: violet500 },
  { tag: [t.labelName], color: violet400 },
  {
    tag: [t.color, t.constant(t.name), t.standard(t.name)],
    color: purple600
  },
  { tag: [t.definition(t.name), t.separator], color: violet700 },
  { tag: [t.brace], color: purple300 },
  {
    tag: [t.annotation],
    color: invalid
  },
  {
    tag: [t.number, t.changed, t.annotation, t.modifier, t.self, t.namespace],
    color: violet500
  },
  {
    tag: [t.typeName, t.className],
    color: purple400
  },
  {
    tag: [t.operator, t.operatorKeyword],
    color: violet700
  },
  {
    tag: [t.tagName],
    color: purple600
  },
  {
    tag: [t.squareBracket],
    color: violet800
  },
  {
    tag: [t.angleBracket],
    color: purple500
  },
  {
    tag: [t.attributeName],
    color: violet400
  },
  {
    tag: [t.regexp],
    color: violet600
  },
  {
    tag: [t.quote],
    color: violet900
  },
  { tag: [t.string], color: purple400 },
  {
    tag: t.link,
    color: violet600,
    textDecoration: 'underline',
    textUnderlinePosition: 'under'
  },
  {
    tag: [t.url, t.escape, t.special(t.string)],
    color: purple500
  },
  { tag: [t.meta], color: violet500 },
  { tag: [t.comment], color: violet700, fontStyle: 'italic' },
  { tag: t.strong, fontWeight: 'bold', color: violet600 },
  { tag: t.emphasis, fontStyle: 'italic', color: violet600 },
  { tag: t.strikethrough, textDecoration: 'line-through' },
  { tag: t.heading, fontWeight: 'bold', color: purple600 },
  { tag: t.special(t.heading1), fontWeight: 'bold', color: purple600 },
  { tag: t.heading1, fontWeight: 'bold', color: purple600 },
  {
    tag: [t.heading2, t.heading3, t.heading4],
    fontWeight: 'bold',
    color: purple600
  },
  {
    tag: [t.heading5, t.heading6],
    color: purple600
  },
  { tag: [t.atom, t.bool, t.special(t.variableName)], color: purple400 },
  {
    tag: [t.processingInstruction, t.inserted],
    color: violet300
  },
  {
    tag: [t.contentSeparator],
    color: violet400
  },
  { tag: t.invalid, color: violet700, borderBottom: `1px dotted ${invalid}` }
])

export const witerLight = [
  witerLightTheme,
  syntaxHighlighting(witerLightHighlightStyle)
]
